
import { TipoDocumentoNameEnum, TipoSolicitudesEnum } from '@/shared/interfaces';
import * as yup from 'yup';

interface IRestriccionTelefono {
  tolerancia_repeticion: number,
  no_permitidos: string[],
  longitud_subgrupos: number
}

interface IValidationResult {
  isValid: boolean;
  message: string;
}


export const validarRestriccionNumeroCelular = (
  schema: yup.StringSchema,
  restriccion?: IRestriccionTelefono
) =>
  schema.test(
    'restriccion-numero',
    'El número ingresado no es permitido',
    function (value) {
      if (!value || !restriccion) {
        return true;
      }

      const tolerancia =
        restriccion.tolerancia_repeticion ?? 0;

      const longitudSubgrupos =
        restriccion.longitud_subgrupos ?? 0;

      // 1. Números repetidos consecutivamente
      if (tolerancia > 0) {
        const regex = new RegExp(
          String.raw`(\d)\1{${tolerancia}}`
        );

        if (regex.test(value)) {
          return this.createError({
            message: `El número ingresado no es permitido. No se permiten más de ${tolerancia} números repetidos consecutivamente.`,
          });
        }
      }

      // 2. Grupos repetidos consecutivamente
      if (longitudSubgrupos > 0) {
        const regex = new RegExp(
          String.raw`(\d{${longitudSubgrupos},})\1+`
        );

        if (regex.test(value)) {
          return this.createError({
            message:
              'El número ingresado no es permitido. No se permiten secuencias repetidas de números.',
          });
        }
      }

      // 3. Secuencias no permitidas
      if (
        restriccion.no_permitidos?.some(
          sequence => value.includes(sequence)
        )
      ) {
        return this.createError({
          message: 'El número ingresado no es permitido',
        });
      }

    return true;
  }
);


export const validateTelefono = (
  value: string | undefined,
  customRestriccionTelefono?: IRestriccionTelefono
): yup.ValidationError | true => {
  if (!value) {
    return true;
  }

  // 1. Validar prefijo telefónico
  const validatePrefix = /^(02|03|04|05|06|07)/;

  if (value.length === 1 && value !== '0') {
    return new yup.ValidationError(
      'Debe ingresar un prefijo telefónico válido, por ejemplo: 02 (Pichincha, Santo Domingo de los Tsáchilas), 04 (Guayas, Santa Elena), etc.'
    );
  }

  // A partir del segundo carácter validar el prefijo completo
  if (value.length >= 2 && !validatePrefix.test(value)) {
    return new yup.ValidationError(
      'Debe ingresar un prefijo telefónico válido, por ejemplo: 02 (Pichincha, Santo Domingo de los Tsáchilas), 04 (Guayas, Santa Elena), etc.'
    );
  }

  if (!customRestriccionTelefono) {
    return true;
  }

  const tolerancia =
    customRestriccionTelefono.tolerancia_repeticion ?? 0;

  const longitudSubgrupos =
    customRestriccionTelefono.longitud_subgrupos ?? 0;

  // 2. No permitir más de N caracteres iguales consecutivos
  if (tolerancia > 0) {
    const regexToleranciaRepeticion = new RegExp(
      String.raw`(\d)\1{${tolerancia}}`
    );

    if (regexToleranciaRepeticion.test(value)) {
      return new yup.ValidationError(
        `El número ingresado no es permitido. No se permiten más de ${tolerancia} números repetidos consecutivamente.`
      );
    }
  }

  // 3. No permitir grupos repetidos consecutivamente
  if (longitudSubgrupos > 0) {
    const regexLongitudSubgrupos = new RegExp(
      String.raw`(\d{${longitudSubgrupos},})\1+`
    );

    if (regexLongitudSubgrupos.test(value)) {
      return new yup.ValidationError(
        'El número ingresado no es permitido. No se permiten secuencias repetidas de números.'
      );
    }
  }

  // 4. No permitir secuencias parametrizadas
  if (
    customRestriccionTelefono.no_permitidos?.some(
      sequence => value.includes(sequence)
    )
  ) {
    return new yup.ValidationError(
      'El número ingresado no es permitido'
    );
  }

  return true;
};


export const validateCelularRestriccion = (
  value?: string,
  customRestriccionCelular?: IRestriccionTelefono
): IValidationResult => {
  if (!value) {
    return {
      isValid: true,
      message: '',
    };
  }

  // 1. Formato de celular
  if (!/^09\d{8}$/.test(value)) {
    return {
      isValid: false,
      message:
        'El número debe contener 10 caracteres y comenzar con "09".',
    };
  }

  // 2. Números repetidos consecutivamente
  const tolerancia =
    customRestriccionCelular?.tolerancia_repeticion ?? 0;

  if (tolerancia > 0) {
    const regexToleranciaRepeticion = new RegExp(
      String.raw`(\d)\1{${tolerancia}}`
    );

    if (regexToleranciaRepeticion.test(value)) {
      return {
        isValid: false,
        message: `El número ingresado no es permitido. No se permiten más de ${tolerancia} números repetidos consecutivamente.`,
      };
    }
  }

  // 3. Grupos repetidos consecutivamente
  const longitudSubgrupos =
    customRestriccionCelular?.longitud_subgrupos ?? 0;

  if (longitudSubgrupos > 0) {
    const regexLongitudSubgrupos = new RegExp(
      String.raw`(\d{${longitudSubgrupos},})\1+`
    );

    if (regexLongitudSubgrupos.test(value)) {
      return {
        isValid: false,
        message:
          'El número ingresado no es permitido. No se permiten secuencias repetidas de números.',
      };
    }
  }

  // 4. Secuencias no permitidas
  if (
    customRestriccionCelular?.no_permitidos?.some(
      sequence => value.includes(sequence)
    )
  ) {
    return {
      isValid: false,
      message: 'El número ingresado no es permitido.',
    };
  }

  return {
    isValid: true,
    message: '',
  };
};

export const validateTelefonoRestriccion = (
  value?: string,
  customRestriccionTelefono?: IRestriccionTelefono
): IValidationResult => {
  if (!value) {
    return {
      isValid: true,
      message: '',
    };
  }

  // 1. Si escribió algo, debe tener mínimo 2 caracteres
  if (value.length <= 1) {
    return {
      isValid: false,
      message: 'Debe ingresar al menos 2 caracteres.',
    };
  }

  // 2. Validar prefijo telefónico
  const validatePrefix = /^(02|03|04|05|06|07)/;

  if (!validatePrefix.test(value)) {
    return {
      isValid: false,
      message:
        'Debe ingresar un prefijo telefónico válido, por ejemplo: 02 (Pichincha, Santo Domingo de los Tsáchilas), 04 (Guayas, Santa Elena), etc.',
    };
  }

  // 3. Números repetidos consecutivamente
  const tolerancia =
    customRestriccionTelefono?.tolerancia_repeticion ?? 0;

  if (tolerancia > 0) {
    const regexToleranciaRepeticion = new RegExp(
      String.raw`(\d)\1{${tolerancia}}`
    );

    if (regexToleranciaRepeticion.test(value)) {
      return {
        isValid: false,
        message: `El número ingresado no es permitido. No se permiten más de ${tolerancia} números repetidos consecutivamente.`,
      };
    }
  }

  // 4. Grupos repetidos consecutivamente
  const longitudSubgrupos =
    customRestriccionTelefono?.longitud_subgrupos ?? 0;

  if (longitudSubgrupos > 0) {
    const regexLongitudSubgrupos = new RegExp(
      String.raw`(\d{${longitudSubgrupos},})\1+`
    );

    if (regexLongitudSubgrupos.test(value)) {
      return {
        isValid: false,
        message:
          'El número ingresado no es permitido. No se permiten secuencias repetidas de números.',
      };
    }
  }

  // 5. Secuencias no permitidas
  if (
    customRestriccionTelefono?.no_permitidos?.some(
      sequence => value.includes(sequence)
    )
  ) {
    return {
      isValid: false,
      message: 'El número ingresado no es permitido.',
    };
  }

  return {
    isValid: true,
    message: '',
  };
};

export const newProspectoSchema = (
  customRestriccionCelular?: IRestriccionTelefono,
) => yup.object({

  //observacion_solicitud: yup.string().required(),
  tipo_identificacion: yup.string().required('Campo Requerido'),
  identificacion: yup.string().required('Campo Requerido').min(10, 'Identificacion debe tener minimo 10 caracteres'),

  //CEDULA
  fecha_nacimiento: yup
    .string()
    .when('tipo_identificacion', {
      is: (tipo_identificacion: string) => TipoDocumentoNameEnum.CEDULA == tipo_identificacion,
      then: schema => schema.required('Campo Obligatorio'),
    })
    .optional(),
  edad: yup
    .number()
    .when('tipo_identificacion', {
      is: (tipo_identificacion: string) => TipoDocumentoNameEnum.CEDULA == tipo_identificacion,
      then: schema => schema.required('Campo Obligatorio').typeError('Campo Obligatorio'),
    })
    .optional(),
  razon_social: yup
    .string()
    .when('tipo_identificacion', {
      is: (tipo_identificacion: string) => TipoDocumentoNameEnum.CEDULA == tipo_identificacion,
      then: schema => schema.test('trim', 'El nombre es Requerido', value => {
        const trimmedValue = value ? value.trim() : '';
        return trimmedValue.length > 0;
      })
        .required('El nombre es Requerido')
        .max(50, 'La longitud máxima es 50 caracteres')
        .matches(/^[^\s].*$/, 'No debe empezar con espacios')
        .matches(/^[\p{L}\s]+$/u, 'Solo se admiten caracteres de la A a la Z'),
    })
    .optional(),
  nacionalidad: yup
    .string()
    .when('tipo_identificacion', {
      is: (tipo_identificacion: string) => TipoDocumentoNameEnum.CEDULA == tipo_identificacion,
      then: schema => schema.required('Campo Obligatorio'),
    })
    .optional(),
  pais: yup
    .string()
    .when('tipo_identificacion', {
      is: (tipo_identificacion: string) => TipoDocumentoNameEnum.CEDULA == tipo_identificacion,
      then: schema => schema.required('Campo Obligatorio'),
    })
    .optional(),

  //RUC
  tipo_contribuyente: yup
    .string()
    .when('tipo_identificacion', {
      is: (tipo_identificacion: string) => TipoDocumentoNameEnum.RUC == tipo_identificacion,
      then: schema => schema.required('Campo Obligatorio'),
    })
    .optional(),
  estado_contribuyente_ruc: yup
    .string()
    .when('tipo_identificacion', {
      is: (tipo_identificacion: string) => TipoDocumentoNameEnum.RUC == tipo_identificacion,
      then: schema => schema.required('Campo Obligatorio'),
    })
    .optional(),
  regimen: yup
    .string()
    .when('tipo_identificacion', {
      is: (tipo_identificacion: string) => TipoDocumentoNameEnum.RUC == tipo_identificacion,
      then: schema => schema.required('Campo Obligatorio'),
    })
    .optional(),
  actividad_economica_principal: yup
    .string()
    .when('tipo_identificacion', {
      is: (tipo_identificacion: string) => TipoDocumentoNameEnum.RUC == tipo_identificacion,
      then: schema => schema.required('Campo Obligatorio'),
    })
    .optional(),

  // celular: yup.string().required('Campo Obligatorio')
  //   .min(10, 'El número de contener 10 caracteres')
  //   .matches(/^09\d{8}$/, 'El número debe comenzar con "09"'),
  celular: validarRestriccionNumeroCelular(
    yup
      .string()
      .required('Campo Obligatorio')
      .min(10, 'El número debe contener 10 caracteres')
      .matches(
        /^09\d{8}$/,
        'El número debe comenzar con "09"'
      ),
    customRestriccionCelular
  ),

  //BURO CREDITO
  puntaje_score: yup.number().required('Campo Requerido').typeError('Campo Requerido'),
  puntaje_score_sobreendeudamiento: yup.number().required('Campo Requerido').typeError('Campo Requerido'),
  puntaje_score_empresa: yup.number().required('Campo Requerido').typeError('Campo Requerido'),
  segmentacion: yup.string().required('Campo Requerido').typeError('Campo Requerido'),
  linea: yup.number().optional().nullable(),
  // es_pyme: yup.boolean().required(),

  ///* solicitud de canje o evento
  solicitud_canje: yup
    .string()
    .when('condicion_venta', {
      is: (condicion_venta: string) => TipoSolicitudesEnum.CANJE == condicion_venta,
      then: schema => schema.required('Campo Obligatorio'),
    })
    .optional(),

  solicitud_evento: yup
    .string()
    .when('condicion_venta', {
      is: (condicion_venta: string) => (
        TipoSolicitudesEnum.EVENTO_GRATUITO == condicion_venta
        // || TipoSolicitudesEnum.EVENTO_PAGADO == condicion_venta
      ),
      then: schema => schema.required('Campo Obligatorio'),
    })
    .optional(),
});

////*Prospecto
export const newProspectoCorporativoSchema = (
  customRestriccionCelular?: IRestriccionTelefono,
  ) => yup.object({

  //observacion_solicitud: yup.string().required(),
  tipo_identificacion: yup.string().required('Campo Requerido'),
  identificacion: yup.string().required('Campo Requerido').min(10, 'Identificacion debe tener minimo 10 caracteres'),

  //CEDULA
  fecha_nacimiento: yup
    .string()
    .when('tipo_identificacion', {
      is: (tipo_identificacion: string) => TipoDocumentoNameEnum.CEDULA == tipo_identificacion,
      then: schema => schema.required('Campo Obligatorio'),
    })
    .optional(),
  edad: yup
    .number()
    .when('tipo_identificacion', {
      is: (tipo_identificacion: string) => TipoDocumentoNameEnum.CEDULA == tipo_identificacion,
      then: schema => schema.required('Campo Obligatorio').typeError('Campo Obligatorio'),
    })
    .optional(),
  razon_social: yup
    .string()
    .when('tipo_identificacion', {
      is: (tipo_identificacion: string) => TipoDocumentoNameEnum.CEDULA == tipo_identificacion,
      then: schema => schema.test('trim', 'El nombre es Requerido', value => {
        const trimmedValue = value ? value.trim() : '';
        return trimmedValue.length > 0;
      })
        .required('El nombre es Requerido')
        .max(50, 'La longitud máxima es 50 caracteres')
        .matches(/^[^\s].*$/, 'No debe empezar con espacios')
        .matches(/^[\p{L}\s]+$/u, 'Solo se admiten caracteres de la A a la Z'),
    })
    .optional(),
  nacionalidad: yup
    .string()
    .when('tipo_identificacion', {
      is: (tipo_identificacion: string) => TipoDocumentoNameEnum.CEDULA == tipo_identificacion,
      then: schema => schema.required('Campo Obligatorio'),
    })
    .optional(),
  pais: yup
    .string()
    .when('tipo_identificacion', {
      is: (tipo_identificacion: string) => TipoDocumentoNameEnum.CEDULA == tipo_identificacion,
      then: schema => schema.required('Campo Obligatorio'),
    })
    .optional(),

  //RUC
  tipo_contribuyente: yup
    .string()
    .when('tipo_identificacion', {
      is: (tipo_identificacion: string) => TipoDocumentoNameEnum.RUC == tipo_identificacion,
      then: schema => schema.required('Campo Obligatorio'),
    })
    .optional(),
  estado_contribuyente_ruc: yup
    .string()
    .when('tipo_identificacion', {
      is: (tipo_identificacion: string) => TipoDocumentoNameEnum.RUC == tipo_identificacion,
      then: schema => schema.required('Campo Obligatorio'),
    })
    .optional(),
  regimen: yup
    .string()
    .when('tipo_identificacion', {
      is: (tipo_identificacion: string) => TipoDocumentoNameEnum.RUC == tipo_identificacion,
      then: schema => schema.required('Campo Obligatorio'),
    })
    .optional(),
  actividad_economica_principal: yup
    .string()
    .when('tipo_identificacion', {
      is: (tipo_identificacion: string) => TipoDocumentoNameEnum.RUC == tipo_identificacion,
      then: schema => schema.required('Campo Obligatorio'),
    })
    .optional(),

  // celular: yup.string().required('Campo Obligatorio')
  //   .min(10, 'El número de contener 10 caracteres')
  //   .matches(/^09\d{8}$/, 'El número debe comenzar con "09"'),
  celular: validarRestriccionNumeroCelular(
    yup
      .string()
      .required('Campo Obligatorio')
      .min(10, 'El número debe contener 10 caracteres')
      .matches(
        /^09\d{8}$/,
        'El número debe comenzar con "09"'
      ),
    customRestriccionCelular
  ),

  // telefono: validateTelefono(
  //   yup.string().required('Campo Obligatorio'),
  //   customRestriccionCelular
  // ),
  telefono: yup
    .string()
    .optional()
    .min(9, 'Debe ingresar al menos 9 caracteres')
    .test(
      'longitud-minima-telefono',
      'Debe ingresar al menos 2 caracteres',
      function (value) {
        // Si está vacío, sigue siendo opcional
        if (!value) {
          return true;
        }

        // Si escribió algo, debe tener mínimo 2 caracteres
        return value.length > 1;
      }
    )
    .test(
    'validar-telefono',
    'El número ingresado no es permitido',
    function (value) {
      const result = validateTelefono(
        value,
        customRestriccionCelular
      );

      if (result instanceof yup.ValidationError) {
        return this.createError({
          message: result.message,
        });
      }

      return true;
    }
  ),

  //BURO CREDITO
  // puntaje_score: yup.number().required('Campo Requerido').typeError('Campo Requerido'),
  // puntaje_score_sobreendeudamiento: yup.number().required('Campo Requerido').typeError('Campo Requerido'),
  // puntaje_score_empresa: yup.number().required('Campo Requerido').typeError('Campo Requerido'),
  // segmentacion: yup.string().required('Campo Requerido').typeError('Campo Requerido'),
  linea: yup.number().optional().nullable(),
  // es_pyme: yup.boolean().required(),

  ///* solicitud de canje o evento
  solicitud_canje: yup
    .string()
    .when('condicion_venta', {
      is: (condicion_venta: string) => TipoSolicitudesEnum.CANJE == condicion_venta,
      then: schema => schema.required('Campo Obligatorio'),
    })
    .optional(),

  solicitud_evento: yup
    .string()
    .when('condicion_venta', {
      is: (condicion_venta: string) => (
        TipoSolicitudesEnum.EVENTO_GRATUITO == condicion_venta
        // || TipoSolicitudesEnum.EVENTO_PAGADO == condicion_venta
      ),
      then: schema => schema.required('Campo Obligatorio'),
    })
    .optional(),
});

export const newProspectoCambioVendedorSchema = yup.object({
  id_prospecto: yup.string().required('Campo Obligatorio'),
  vendedor_actual: yup.string().optional(),
  vendedor_nuevo: yup.string().required('Campo Obligatorio')
});

export const newEditarProspecteoSchema = yup.object({
  id_prospecto: yup.string().required('Campo Obligatorio'),
  celular: yup.string().required('Campo Obligatorio')
    .min(10, 'El número de contener 10 caracteres')
    .matches(/^09\d{8}$/, 'El número debe comenzar con "09"'),
});

export const newMotivoProspecteoSchema = yup.object({
  id_prospecto: yup.string().required('Campo Obligatorio'),
  motivo_rechazo: yup.string().required('Campo Obligatorio'),
});

export const newSolicitudIngresoBenificio = yup.object({
  observacion_supervisor: yup.string().when('es_reportado', {
    is: (es_reportado: boolean) => es_reportado,
    then: schema => schema.required('Campo Obligatorio')
  }).optional().nullable()
})